import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useMemo, useState} from 'react'
import WorkingAppContext from "../../Shared/contexts/WorkingAppContext";
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import getTitle from "../../../helpers/getTitle";
import {NullAsDefault} from "../../Shared";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import DrawDialog from "../../../components/drawer/dialog/DrawDialog";
import {FilterPill} from "./FilterPill";
import classNames from "classnames";
import {MdOutlineFilterAltOff} from "react-icons/md";
import {FaUsers} from "react-icons/fa6";
import {useToggle} from "react-use"
import {IoChevronDownOutline, IoChevronUpOutline} from "react-icons/io5"
import {PiUserCircleCheckFill} from "react-icons/pi"

// Chọn group mặc định: ưu tiên 1) group đang active_as_current (context hiện tại của user),
// 2) trong các group mình đang thực sự là active member (active_as_member?.id) — ưu tiên role_id thấp nhất
// tức vai trò cao nhất (owner=1 > admin=2 > member=3) nếu có, không thì lấy group member đầu tiên
// — backend không có field "priority" riêng, role_id là field gần nhất thể hiện "độ chính" của quan hệ member-group.
// 3) fallback: lấy phần tử đầu (trường hợp admin xem group mà mình không phải member).
const pickDefaultDepartment = (options) => {
    if (!options?.length) return null
    const current = options.find(e => !!e?.active_as_current)
    if (current) return current
    const memberOptions = options.filter(e => !!e?.active_as_member?.id)
    if (memberOptions.length > 0) {
        const withRole = memberOptions.filter(e => e?.active_as_member?.role_id != null)
        if (withRole.length > 0) {
            return withRole.reduce((best, cur) =>
                cur.active_as_member.role_id < best.active_as_member.role_id ? cur : best
            )
        }
        return memberOptions[0]
    }
    return options[0]
}

const DepartmentFilteredHeader = ({
                                      title = null,
                                      filters = ["group"],
                                      type,
                                      extra = null,
                                  }) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const {
        viewScope = 'selected',
        activeProfile = null,
        activeOrganization = null,
        updateWorkingAppState = NullAsDefault,
        selectedDepartmentId: selectedDepartmentIdState,
        selectedDepartment,
        groups: departmentOptionsState = []
    } = useContext(WorkingAppContext)
    const [departmentOptions, setDepartmentOptions] = useState(departmentOptionsState);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(selectedDepartmentIdState);

    const [isToggle, toggle] = useToggle(true)

    // Tên gọi "group/ministry" phụ thuộc org_type — vd Giáo xứ: "Hội đoàn", Giáo phận: "Ủy ban", Dòng tu: "Cộng đoàn"...
    const deptLabel = useOrgTypeLabel(activeOrganization?.org_type, 'group', activeOrganization?.label_privacy) || t('groups / ministries')

    // Admin của organization thấy TẤT CẢ group/ministry; member thường chỉ thấy group mình đang tham gia
    const isOrgAdmin = !!activeOrganization?.edit
    const visibleDepartmentOptions = useMemo(
        () => isOrgAdmin ? departmentOptions : departmentOptions?.filter(e => e?.active_as_member?.id),
        [departmentOptions, isOrgAdmin]
    )

    useEffect(() => {
        setDepartmentOptions(departmentOptionsState);
    }, [departmentOptionsState]);

    useEffect(() => {
        const _default = pickDefaultDepartment(visibleDepartmentOptions)
        updateWorkingAppState({
            selectedDepartmentId: _default?.id,
            selectedDepartment: _default
        });
        setSelectedDepartmentId(_default?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [departmentOptions, isOrgAdmin]);

    if (viewScope === "organization") return null;

    const selectedDepartmentObj = visibleDepartmentOptions?.find(e => e?.id === selectedDepartmentId)

    return (
        <header
            style={{
                margin: !isLg && '0 -0.65rem',
                top: !isLg && '-0.75rem',
            }}
            className="sticky background shadow-sm rounded-2xl z-20">
            {!!title && (
                isLg ? (
                    <div className="px-4 py-2.5 border-b border-color-50 flex items-center gap-2 rounded-t-2xl">
                        <span className="flex-1 font-bold text-sm text-color-300 uppercase tracking-wider">
                            {t(title)}
                        </span>
                        {extra}
                    </div>
                ) : (
                    <div className={`mx-4 pt-1 flex items-center gap-2 ${!isToggle ? 'pb-3' : ''}`}>
                        <button
                            type="button"
                            onClick={toggle}
                            className="flex items-center gap-1.5 flex-1 min-w-0 p-1 -ml-1">
                            <span className="font-bold text-sm text-color-300 uppercase tracking-wider truncate">
                                {t(title)}
                            </span>
                            {isToggle
                                ? <IoChevronUpOutline size={14} className="text-indigo-500 flex-shrink-0"/>
                                : <IoChevronDownOutline size={14} className="text-color-400 flex-shrink-0"/>
                            }
                        </button>
                        {extra}
                    </div>
                )
            )}
            {(isLg || isToggle) && (
            <div className="px-4 pb-3 pt-2 flex flex-col gap-3">
                <div className="flex flex-col gap-3">
                    {(filters || []).includes("group") && (
                        <div className="flex flex-col gap-1">
                            <DrawDialog
                                width={540}
                                title={
                                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                                        {deptLabel}
                                    </div>
                                }
                                closable={true}
                                container="root"
                                component={
                                    <FilterPill
                                        icon={FaUsers}
                                        placeholder={deptLabel}
                                        value={selectedDepartmentObj ? getTitle(selectedDepartmentObj) : null}
                                    />
                                }>
                                {(isToggle, toggle) => isToggle && (
                                    <div className="flex flex-col gap-3">
                                        <div className="font-medium text-color-300 tracking-wide uppercase">
                                            {deptLabel}
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            {visibleDepartmentOptions
                                                ?.map((cls, clsIndex) => {
                                                    return (
                                                        <div key={clsIndex}
                                                             onClick={() => {
                                                                 updateWorkingAppState({
                                                                     selectedDepartmentId: cls?.id,
                                                                     selectedDepartment: cls,
                                                                 });
                                                                 setSelectedDepartmentId(cls?.id);
                                                                 toggle();
                                                             }}
                                                             style={{minHeight: 44}}
                                                             className={classNames(
                                                                 "px-3 py-2 flex items-center gap-2.5 rounded-xl border transition-colors cursor-pointer",
                                                                 selectedDepartmentId === cls?.id
                                                                     ? 'border-indigo-300 bg-indigo-50'
                                                                     : 'border-color-50 hover:border-indigo-200 hover:bg-indigo-50'
                                                             )}>
                                                            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                                                                <FaUsers size={14} className="text-indigo-400"/>
                                                            </div>
                                                            <div className="flex flex-col flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className={classNames(
                                                                        'font-semibold text-sm leading-snug truncate',
                                                                        cls?.active_as_member?.id ? 'text-color-000' : 'text-color-300'
                                                                    )}>
                                                                        {getTitle(cls)}
                                                                    </span>
                                                                    {cls?.active_as_member?.id && (
                                                                        <PiUserCircleCheckFill size={13} className="text-emerald-500 flex-shrink-0"/>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1.5 leading-snug">
                                                                    {cls.group_title && (
                                                                        <span className="text-xs text-indigo-400 italic truncate">
                                                                            {cls.group_title}
                                                                        </span>
                                                                    )}
                                                                    {cls?.member_count != null && (
                                                                        <span className="text-xs text-color-400 flex-shrink-0">
                                                                            {cls.group_title && '· '}{cls.member_count} {t('members')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                        </div>
                                        <div
                                            onClick={() => {
                                                updateWorkingAppState({
                                                    selectedDepartmentId: null,
                                                    selectedDepartment: null
                                                });
                                                setSelectedDepartmentId(null)
                                                toggle();
                                            }}
                                            style={{minHeight: 44}}
                                            className="px-3 py-2 flex items-center gap-2.5 rounded-xl border border-color-50 background-50 hover:bg-red-50 hover:border-red-200 transition-colors cursor-pointer">
                                            <div className="w-8 h-8 rounded-lg background border border-color-100 flex items-center justify-center flex-shrink-0">
                                                <MdOutlineFilterAltOff size={14} className="text-color-300"/>
                                            </div>
                                            <span className="font-semibold text-sm text-color-200">{t("select all")}</span>
                                        </div>
                                    </div>
                                )}
                            </DrawDialog>
                        </div>
                    )}
                </div>
            </div>
            )}
        </header>
    );
}

export default DepartmentFilteredHeader
